import { useState } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getApiBaseUrl, getAuthHeaders } from "@/lib/api";
import { csvEscape, downloadCsv, toCsv } from "@/lib/utils/csv";

// Google Ads conversion action names — must exactly match the names in Google Ads
const GOOGLE_CONVERSION_NAMES: Record<string, string> = {
  lead: "Lagt igjen epost",
  contact: "Registrering_API",
  initiate_checkout: "Start betaling",
  purchase: "Kjøp av abonnement",
};

interface ConversionRow {
  id: string;
  eventType: string;
  createdAt: string;
  platform: "ios" | "android" | "web" | null;
  enrichedData: {
    email?: string;
    tier?: string;
    value?: number;
    currency?: string;
  } | null;
  attribution: {
    gclid?: string | null;
  } | null;
  metaDelivered: boolean;
  googleDelivered: boolean;
  tiktokDelivered: boolean;
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: formatDate(from), to: formatDate(to) };
}

// Format to Google Ads expected format: "yyyy-MM-dd HH:mm:ss" (timezone set in Parameters row)
function formatGoogleTime(isoString: string): string {
  return isoString.replace("T", " ").replace(/\.\d{3}Z$/, "");
}

// Standard gclid-based conversion import
function generateGclidCsv(rows: ConversionRow[]): string {
  const withGclid = rows.filter((r) => r.attribution?.gclid);
  const lines = [
    "Parameters:TimeZone=Etc/GMT",
    "Google Click ID,Conversion Name,Conversion Time,Conversion Value,Conversion Currency,Ad User Data,Ad Personalization",
  ];
  for (const row of withGclid) {
    const name = GOOGLE_CONVERSION_NAMES[row.eventType] ?? row.eventType;
    const value = row.enrichedData?.value ?? "";
    const currency = row.enrichedData?.currency ?? "";
    lines.push(
      [
        csvEscape(row.attribution!.gclid!),
        csvEscape(name),
        formatGoogleTime(row.createdAt),
        value,
        currency,
        "Granted",
        "Granted",
      ].join(",")
    );
  }
  return lines.join("\n");
}

// Enhanced Conversions for Leads — all rows with email, includes gclid when available
function generateEnhancedCsv(rows: ConversionRow[]): string {
  const withEmail = rows.filter((r) => r.enrichedData?.email);
  const lines = [
    "Parameters:TimeZone=Etc/GMT",
    "Google Click ID,Email,Phone Number,Conversion Name,Conversion Time,Conversion Value,Conversion Currency,Ad User Data,Ad Personalization",
  ];
  for (const row of withEmail) {
    const email = row.enrichedData!.email!.toLowerCase().trim();
    const gclid = row.attribution?.gclid ?? "";
    const name = GOOGLE_CONVERSION_NAMES[row.eventType] ?? row.eventType;
    const value = row.enrichedData?.value ?? "";
    const currency = row.enrichedData?.currency ?? "";
    lines.push(
      [
        csvEscape(gclid),
        csvEscape(email),
        "",
        csvEscape(name),
        formatGoogleTime(row.createdAt),
        value,
        currency,
        "Granted",
        "Granted",
      ].join(",")
    );
  }
  return lines.join("\n");
}

// Full dump for analysis in Excel/Sheets — not a Google Ads upload format.
function generateAllDataCsv(rows: ConversionRow[]): string {
  return toCsv(
    [
      "Tidspunkt",
      "Type",
      "Google-navn",
      "E-post",
      "Verdi",
      "Valuta",
      "Gclid",
      "Plattform",
      "Google",
      "Meta",
      "TikTok",
    ],
    rows.map((r) => [
      r.createdAt,
      r.eventType,
      GOOGLE_CONVERSION_NAMES[r.eventType] ?? r.eventType,
      r.enrichedData?.email ?? "",
      r.enrichedData?.value ?? "",
      r.enrichedData?.currency ?? "",
      r.attribution?.gclid ?? "",
      r.platform ?? "",
      r.googleDelivered ? "ja" : "nei",
      r.metaDelivered ? "ja" : "nei",
      r.tiktokDelivered ? "ja" : "nei",
    ])
  );
}

export function ConversionsTab() {
  const defaults = defaultDateRange();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [rows, setRows] = useState<ConversionRow[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFetch = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/system/conversions?from=${fromDate}&to=${toDate}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as ConversionRow[];
      setRows(data);
      toast.success(`${data.length} konverteringer hentet`);
    } catch (err: any) {
      toast.error(err.message || "Henting feilet");
    } finally {
      setLoading(false);
    }
  };

  const withEmail = rows.filter((r) => r.enrichedData?.email);
  const withGclid = rows.filter((r) => r.attribution?.gclid);

  const handleDownloadEmail = () => {
    if (withEmail.length === 0) {
      toast.error("Ingen konverteringer med e-post å eksportere");
      return;
    }
    const csv = generateEnhancedCsv(rows);
    downloadCsv(csv, `google-email-conversions-${fromDate}-${toDate}.csv`);
    toast.success(`${withEmail.length} rader eksportert`);
  };

  const handleDownloadGclid = () => {
    const csv = generateGclidCsv(rows);
    downloadCsv(csv, `google-gclid-conversions-${fromDate}-${toDate}.csv`);
    toast.success(`${withGclid.length} gclid-rader eksportert`);
  };

  const handleDownloadAll = () => {
    const csv = generateAllDataCsv(rows);
    downloadCsv(csv, `alle-konverteringer-${fromDate}-${toDate}.csv`, {
      bom: true,
    });
    toast.success(`${rows.length} rader eksportert`);
  };

  const countByType = (type: string) =>
    rows.filter((r) => r.eventType === type).length;
  const countWithEmailByType = (type: string) =>
    rows.filter((r) => r.eventType === type && r.enrichedData?.email).length;

  return (
    <div className="space-y-4 mt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Google Ads konverteringseksport
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-sm text-admin-text-secondary">
                Fra dato
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-admin-text-secondary">
                Til dato
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button onClick={handleFetch} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Hent konverteringer
            </Button>
            {withEmail.length > 0 && (
              <Button
                variant="outline"
                onClick={handleDownloadEmail}
              >
                <Download className="h-4 w-4 mr-1" />
                E-post CSV ({withEmail.length})
              </Button>
            )}
            {withGclid.length > 0 && (
              <Button
                variant="outline"
                onClick={handleDownloadGclid}
              >
                <Download className="h-4 w-4 mr-1" />
                Gclid CSV ({withGclid.length})
              </Button>
            )}
            {rows.length > 0 && (
              <Button variant="outline" onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-1" />
                Alle data CSV ({rows.length})
              </Button>
            )}
          </div>
          <p className="text-xs text-admin-text-secondary">
            E-post CSV: for opplasting via "Optimaliserte konverteringer".
            Gclid CSV: for standard konverteringsimport. Rader med begge deler
            havner i begge filer. Alle data CSV: full tabell inkludert
            plattform, for analyse — ikke for opplasting til Google Ads.
          </p>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <>
          {/* Summary */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-4 gap-4 text-sm">
                {(
                  ["lead", "contact", "initiate_checkout", "purchase"] as const
                ).map((type) => (
                  <div key={type} className="text-center">
                    <div className="text-admin-text-secondary text-xs mb-1">
                      {GOOGLE_CONVERSION_NAMES[type]}
                    </div>
                    <div className="text-lg font-semibold">
                      {countByType(type)}
                    </div>
                    <div className="text-xs text-admin-text-secondary">
                      {countWithEmailByType(type)} med e-post
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Alle konverteringer ({rows.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tidspunkt</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Google-navn</TableHead>
                    <TableHead>E-post</TableHead>
                    <TableHead>Verdi</TableHead>
                    <TableHead>Gclid</TableHead>
                    <TableHead>Plattform</TableHead>
                    <TableHead>Google</TableHead>
                    <TableHead>Meta</TableHead>
                    <TableHead>TikTok</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className={
                        !r.enrichedData?.email ? "opacity-40" : undefined
                      }
                    >
                      <TableCell className="text-xs font-mono">
                        {new Date(r.createdAt).toLocaleString("nb-NO")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.eventType}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {GOOGLE_CONVERSION_NAMES[r.eventType] ?? r.eventType}
                      </TableCell>
                      <TableCell className="text-xs max-w-[180px] truncate">
                        {r.enrichedData?.email ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.enrichedData?.value != null
                          ? `${r.enrichedData.value} ${r.enrichedData?.currency ?? ""}`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs font-mono max-w-[100px] truncate">
                        {r.attribution?.gclid ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.platform ?? "—"}
                      </TableCell>
                      <TableCell>
                        {r.googleDelivered ? (
                          <Badge variant="default">OK</Badge>
                        ) : (
                          <Badge variant="outline">Nei</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.metaDelivered ? (
                          <Badge variant="default">OK</Badge>
                        ) : (
                          <Badge variant="outline">Nei</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.tiktokDelivered ? (
                          <Badge variant="default">OK</Badge>
                        ) : (
                          <Badge variant="outline">Nei</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
